import React, { useEffect, useCallback, useMemo } from 'react';
import LoaderSpinner from '../common/Loader';
import { useNavigate, useLocation } from 'react-router-dom';
import { TextField, MenuItem, Select, FormControl } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Modal from 'react-bootstrap/Modal';
import ReactSelect from "react-select";
import { emailPlaceholders, getAllTemplates, getRecipientGroups, getTemplateById, sendBroadcast, sendIndividualEmails, saveAndSendEmail, postTemplate } from '../../store/actions/auth';
import { toast } from 'react-toastify';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import AlertDialogModal from '../common/AlertDialog';
import { OtherRecipientEmailsInput, buildOtherRecipientOption, countValidEmails, hasOtherRecipient, reportSendResult } from './otherRecipients';
import { REPLY_TO_ERROR_MESSAGE, SENDER_FIELD_MAX_LENGTH, getSenderFields, getSenderPayload, isInvalidReplyToEmail } from './senderFields';
import { insertTextAtCursor } from './editorUtils';
import AttachmentsField from './AttachmentsField';
import { getAttachmentPayload, toAttachment } from './attachments';
const useStyles = makeStyles((theme) => ({
    formRow: {
        display: 'flex',
        alignItems: 'flex-start',
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
        },
    },
    label: {
        width: 160,
        minWidth: 160,
        textAlign: 'right',
        paddingRight: theme.spacing(2),
        paddingTop: 9,
        fontWeight: 500,
        color: '#333',
        flexShrink: 0,
        [theme.breakpoints.down('sm')]: {
            width: '100%',
            minWidth: 'unset',
            textAlign: 'left',
            paddingBottom: theme.spacing(0.5),
            paddingRight: 0,
        },
    },
    fieldWrapper: {
        flex: 1,
    },
    select: {
        width: '100%',
        backgroundColor: '#fff',
    },
    textField: {
        width: '100%',
        backgroundColor: '#fff',
    },
    quillWrapper: {
        flex: 1,
    },
}));

const ComposeEmail = () => {
    const userDetails = useSelector((state) => state.auth.user);
    const classes = useStyles();
    const [showLoader, setShowLoader] = React.useState(false);
    const [formData, setFormData] = React.useState({
        recipients: [],
        templateId: 0,
        templateName: '',
        subject: '',
        recipientEmailsList: [],
        otherEmails: '',
        body: '',
        fromName: '',
        replyToEmail: '',
    });
    const [emailInput, setEmailInput] = React.useState('');
    const [attachments, setAttachments] = React.useState([]);
    const [uploadingAttachments, setUploadingAttachments] = React.useState(false);
    const [loadingAttachments, setLoadingAttachments] = React.useState(false);
    const attachmentRequestRef = React.useRef(0);
    const iframeRef = React.useRef(null);
    const [recipientList, setRecipientList] = React.useState([]);
    const [templateList, setTemplateList] = React.useState([]);
    const [showAlert, setShowAlert] = React.useState(false);
    const [alertAction, setAlertAction] = React.useState('send');
    const [linkEditor, setLinkEditor] = React.useState({ show: false, href: '', text: '', anchor: null });
    const [saveTemplateModal, setSaveTemplateModal] = React.useState({ show: false, name: '' });
    let navigate = useNavigate();
    const location = useLocation();

    const [emailPlaceholderList, setEmailPlaceholderList] = React.useState([]);

    const sendOnly = !!location.state?.sendOnly;

    const userRole = useMemo(() => {
        const roleName = (userDetails?.Value?.roleName || '').toString().trim().toLowerCase();
        const role = (userDetails?.Value?.role || '').toString().trim().toLowerCase();
        if (userDetails?.Value?.isSuperAdmin) return 'superadmin';
        if (roleName === 'admin' || role === 'admin') return 'admin';
        if (roleName === 'seller' || role === 'seller') return 'seller';
        if (roleName === 'warehouse manager' || role === 'warehouse manager') return 'warehousemanager';
        if (roleName === 'warehouse worker' || role === 'warehouse worker') return 'warehouseworker';
        return roleName || role;
    }, [userDetails]);

    const isSellerOrAdmin = useMemo(() => {
        return ['admin', 'seller'].includes(userRole);
    }, [userRole]);

    const canSaveTemplate = useMemo(() => ['superadmin', 'admin', 'seller'].includes(userRole), [userRole]);
    const canSendBroadcast = useMemo(() => userRole === 'superadmin', [userRole]);
    const canSendIndividual = useMemo(() => ['superadmin', 'admin', 'seller'].includes(userRole), [userRole]);
    const canSaveAndSend = useMemo(() => ['superadmin', 'admin', 'seller'].includes(userRole), [userRole]);
    const showSaveAndSend = canSaveAndSend && (formData.templateId > 0 || formData.templateName.trim());
    const canSend = isSellerOrAdmin ? canSendIndividual : canSendBroadcast;

    const fetchTemplates = useCallback(async () => {
        setShowLoader(true);
        try {
            const response = await getAllTemplates();
            setTemplateList(response.data.EntityList || []);
        } catch {
            toast.error('An error occurred while fetching template details.');
        } finally {
            setShowLoader(false);
        }
    }, []);

    const fetchAllRecipientList = useCallback(async () => {
        setShowLoader(true);
        try {
            const response = await getRecipientGroups();
            const listWithAllOption = response.data.EntityList.map(group => ({ ...group, value: group.id, label: `${group.name} - ${group.description}` }));
            setRecipientList(listWithAllOption);
        } catch {
            toast.error('An error occurred while fetching recipient groups.');
        } finally {
            setShowLoader(false);
        }
    }, []);

    const fetchemailPlaceholders = useCallback(async () => {
        try {
            const response = await emailPlaceholders();
            if (response?.data?.Status === 200) {
                setEmailPlaceholderList(response?.data?.EntityList || []);
            }
        } catch (error) {
            console.error('Error fetching email placeholders:', error);
        }
    }, []);

    useEffect(() => {
        fetchemailPlaceholders();
        fetchAllRecipientList();
        fetchTemplates();
    }, [fetchemailPlaceholders, fetchAllRecipientList, fetchTemplates]);

    const attachmentsBusy = uploadingAttachments || loadingAttachments;

    // The templates list only carries an attachment count, so the files are fetched when a template with attachments is chosen.
    // Send stays disabled meanwhile so a send cannot go out before the template's saved attachments are in place.
    const loadTemplateAttachments = useCallback(async (template) => {
        const requestId = attachmentRequestRef.current + 1;
        attachmentRequestRef.current = requestId;
        if (!template?.id || !(template.attachmentCount > 0)) {
            setAttachments((template?.attachments || []).map(toAttachment));
            setLoadingAttachments(false);
            return;
        }
        setLoadingAttachments(true);
        try {
            const response = await getTemplateById(template.id);
            if (attachmentRequestRef.current !== requestId) return;
            if (response?.data?.Status === 200) {
                setAttachments((response.data.Entity?.attachments || []).map(toAttachment));
            } else {
                toast.error('Unable to load the template attachments.');
            }
        } catch {
            if (attachmentRequestRef.current === requestId) toast.error('Unable to load the template attachments.');
        } finally {
            if (attachmentRequestRef.current === requestId) setLoadingAttachments(false);
        }
    }, []);

    useEffect(() => {
        const selectedTemplate = location.state?.selectedTemplate;
        if (selectedTemplate) {
            setFormData((prev) => ({
                ...prev,
                templateId: selectedTemplate.id,
                templateName: selectedTemplate.name || '',
                subject: selectedTemplate.subject || '',
                body: selectedTemplate.body || '',
                ...getSenderFields(selectedTemplate),
            }));
            writeToIframe(selectedTemplate.body || '');
            loadTemplateAttachments(selectedTemplate);
        } else {
            // Without a template the editor must still be initialised, otherwise it is not editable.
            writeToIframe('');
        }
    }, [location.state, loadTemplateAttachments]);

    // Pre-fill the "Other" recipients when arriving from the User Requests screen (single or bulk).
    const prefillAppliedRef = React.useRef(false);
    useEffect(() => {
        const prefillEmails = location.state?.recipientEmails;
        if (prefillAppliedRef.current || !Array.isArray(prefillEmails) || prefillEmails.length === 0 || isSellerOrAdmin) return;
        const otherOption = buildOtherRecipientOption(recipientList);
        if (!otherOption) return;
        prefillAppliedRef.current = true;
        setFormData((prev) => ({
            ...prev,
            recipients: [otherOption],
            otherEmails: prefillEmails.join(', '),
        }));
    }, [location.state, recipientList, isSellerOrAdmin]);

    const showOtherEmails = !isSellerOrAdmin && hasOtherRecipient(formData.recipients);

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    const addEmail = (email) => {
        if (!email) return;
        if (!isValidEmail(email)) {
            toast.error(`"${email}" is not a valid email address.`);
            return;
        }
        if (formData.recipientEmailsList.includes(email)) {
            setEmailInput('');
            return;
        }
        handleChange('recipientEmailsList', [...formData.recipientEmailsList, email]);
        setEmailInput('');
    };

    const removeEmail = (email) => {
        handleChange('recipientEmailsList', formData.recipientEmailsList.filter((e) => e !== email));
    };

    const handleEmailInputKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            addEmail(emailInput.trim());
        }
    };

    const getIframeDocument = () => {
        const iframe = iframeRef.current;
        return iframe?.contentDocument || iframe?.contentWindow?.document;
    };

    const getIframeBody = useCallback(() => {
        const doc = getIframeDocument();
        return doc?.body?.innerHTML || '';
    }, []);

    const writeToIframe = useCallback((html) => {
        const iframe = iframeRef.current;
        if (!iframe) return;
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (!doc) return;
        doc.open();
        doc.write(`<!DOCTYPE html><html><head><style>body { margin: 0; padding: 0; font-family: Arial, sans-serif; }</style></head><body>${html}</body></html>`);
        doc.close();
        doc.designMode = 'on';
        // Sync changes back to formData on input
        doc.body.addEventListener('input', () => {
            handleChange('body', doc.body.innerHTML);
        });
        // Allow editing link URLs on double-click
        doc.addEventListener('dblclick', (e) => {
            const anchor = e.target.closest('a');
            if (!anchor) return;
            e.preventDefault();
            e.stopPropagation();
            setLinkEditor({
                show: true,
                href: anchor.getAttribute('href') || '',
                text: anchor.textContent || '',
                anchor,
            });
        });
        // Prevent links from navigating away on single click
        doc.addEventListener('click', (e) => {
            const anchor = e.target.closest('a');
            if (anchor) {
                e.preventDefault();
            }
        });
    }, []);

    const insertDynamicValue = (variable) => {
        if (!variable) return;
        const doc = getIframeDocument();
        if (!doc?.body) return;
        if (insertTextAtCursor(iframeRef.current, variable)) {
            handleChange('body', doc.body.innerHTML);
        }
    };

    const validateForm = () => {
        // Sync latest iframe content before validation
        const latestBody = getIframeBody();
        if (latestBody !== formData.body) {
            handleChange('body', latestBody);
        }
        if (!isSellerOrAdmin && (!formData.recipients || formData.recipients.length === 0)) {
            toast.error('At least one recipient is required.');
            return false;
        }
        if (showOtherEmails && countValidEmails(formData.otherEmails) === 0) {
            toast.error('Enter at least one valid email address for the "Other" recipients.');
            return false;
        }
        if (isSellerOrAdmin && formData.recipientEmailsList.length === 0) {
            toast.error('Recipient email(s) are required for your role.');
            return false;
        }
        if (!formData.subject.trim()) {
            toast.error('Subject is required.');
            return false;
        }
        const bodyContent = latestBody || formData.body || '';
        const strippedBody = bodyContent.replaceAll(/<[^>]*>/g, '').trim();
        if (!strippedBody) {
            toast.error('Body is required.');
            return false;
        }
        if (isInvalidReplyToEmail(formData.replyToEmail)) {
            toast.error(REPLY_TO_ERROR_MESSAGE);
            return false;
        }
        return true;
    };

    const validateSaveForm = () => {
        if (!formData.subject.trim()) {
            toast.error('Subject is required.');
            return false;
        }
        if (isInvalidReplyToEmail(formData.replyToEmail)) {
            toast.error(REPLY_TO_ERROR_MESSAGE);
            return false;
        }
        return true;
    };

    const validateSaveAndSendForm = () => {
        if (!formData.templateName.trim()) {
            toast.error('Template name is required for Save & Send.');
            return false;
        }
        return validateForm();
    };

    const flushEmailInput = () => {
        if (emailInput.trim()) {
            if (!isValidEmail(emailInput.trim())) {
                toast.error(`"${emailInput.trim()}" is not a valid email address.`);
                return false;
            }
            addEmail(emailInput.trim());
            return false; // re-click to confirm after flush
        }
        return true;
    };

    const buildPayload = () => {
        const currentBody = getIframeBody() || formData.body;
        let payload = {
            sendMode: 'Broadcast',
            recipientGroupIds: _.map(formData.recipients, 'value'),
            subject: formData.subject.trim(),
            body: currentBody,
            templateId: formData.templateId || 0,
            templateName: formData.templateName,
            fromName: formData.fromName.trim() || undefined,
            replyToEmail: formData.replyToEmail.trim() || undefined,
            attachments: getAttachmentPayload(attachments),
        };
        if (isSellerOrAdmin) {
            payload = {
                ...payload,
                sendMode: 'Individual',
                recipientEmails: formData.recipientEmailsList.join(', '),
            };
        } else if (showOtherEmails) {
            payload = { ...payload, recipientEmails: formData.otherEmails.trim() };
        }
        return payload;
    };

    // --- Send ---
    const handleSendClick = () => {
        if (!flushEmailInput()) return;
        if (!validateForm()) return;
        setAlertAction('send');
        setShowAlert(true);
    };

    const handleSend = async () => {
        setShowAlert(false);
        setShowLoader(true);
        try {
            const payload = buildPayload();
            let response = await (isSellerOrAdmin ? sendIndividualEmails(payload) : sendBroadcast(payload));
            reportSendResult(response);
        } catch {
            toast.error('An error occurred while sending the email.');
        } finally {
            setShowLoader(false);
        }
    };

    // --- Save Template ---
    const closeSaveTemplateModal = () => setSaveTemplateModal({ show: false, name: '' });

    // The template name is collected in a popup; an already-loaded template keeps its name as the default.
    const handleSaveTemplateClick = () => {
        if (!validateSaveForm()) return;
        setSaveTemplateModal({ show: true, name: formData.templateName || '' });
    };

    const handleSaveTemplate = async () => {
        const templateName = saveTemplateModal.name.trim();
        if (!templateName) {
            toast.error('Template name is required.');
            return;
        }
        closeSaveTemplateModal();
        setShowLoader(true);
        try {
            const payload = {
                id: formData.templateId || 0,
                name: templateName,
                subject: formData.subject,
                body: getIframeBody() || formData.body,
                attachments: getAttachmentPayload(attachments),
                ...getSenderPayload(formData),
            };
            const response = await postTemplate(payload);
            if (response?.data?.Status === 200) {
                toast.success('Template saved successfully!');
                setFormData((prev) => ({
                    ...prev,
                    templateName,
                    templateId: prev.templateId || response.data.Entity?.id || 0,
                }));
                fetchTemplates();
            } else {
                toast.error(response?.data?.Message?.AppStatusDescription || 'Failed to save template.');
            }
        } catch {
            toast.error('An error occurred while saving the template.');
        } finally {
            setShowLoader(false);
        }
    };

    // --- Save & Send ---
    const handleSaveAndSendClick = () => {
        if (!flushEmailInput()) return;
        if (!validateSaveAndSendForm()) return;
        setAlertAction('saveAndSend');
        setShowAlert(true);
    };

    const handleSaveAndSend = async () => {
        setShowAlert(false);
        setShowLoader(true);
        try {
            const payload = buildPayload();
            const response = await saveAndSendEmail(payload);
            reportSendResult(response, {
                successMessage: 'Email saved and sent successfully!',
                failureMessage: 'Failed to save and send email.',
            });
        } catch {
            toast.error('An error occurred while saving and sending the email.');
        } finally {
            setShowLoader(false);
        }
    };

    const handleMultipleSelectChange = (value, field) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleTemplateSelect = (e) => {
        const selectedId = e.target.value;
        const selected = templateList.find(t => t.id === selectedId);
        if (selected) {
            setFormData((prev) => ({
                ...prev,
                templateId: selected.id,
                templateName: selected.name,
                subject: selected.subject,
                body: selected.body,
                ...getSenderFields(selected),
            }));
            writeToIframe(selected.body);
            loadTemplateAttachments(selected);
        } else {
            setFormData((prev) => ({
                ...prev,
                templateId: 0,
                templateName: '',
                subject: '',
                body: '',
                ...getSenderFields(),
            }));
            writeToIframe('');
            loadTemplateAttachments(null);
        }
    };

    return (
        <main>
            <div className="main__container">
                <div className="main__tile_heading cus-btn-include-sec">
                    <span>
                        Compose Email
                        <div className='infoPermission'>
                            Create and customize email content to effectively communicate, ensuring your messages are clear, engaging, and impactful.
                        </div>
                    </span>
                    <div className="top_right_button_container">
                        <div className="top_right_button_container">
                            {canSaveTemplate && !sendOnly && (
                                <button disabled={showLoader || attachmentsBusy} className="btn cus-seconday-bg-btn btn mr-2" onClick={handleSaveTemplateClick}>
                                    Save Template
                                </button>
                            )}
                            {showSaveAndSend && !sendOnly && (
                                <button disabled={showLoader || attachmentsBusy} className="btn cus-seconday-bg-btn btn mr-2" onClick={handleSaveAndSendClick}>
                                    Save &amp; Send
                                </button>
                            )}
                            {canSend && (
                                <button disabled={showLoader || attachmentsBusy} className="btn cus-seconday-bg-btn btn mr-2" onClick={handleSendClick}>
                                    Send Email
                                </button>
                            )}
                            <button disabled={showLoader} className="btn cus-primary-transparent-btn bckbtn" onClick={() => navigate('/email-templates')}>
                                Back
                            </button>
                        </div>
                    </div>
                </div>
                {showLoader && <LoaderSpinner />}
                <div className="cus-page-content-sec">
                    <div className='content-container pt-4'>
                        <div className={classes.formRow}>
                            <div className={classes.label}>Select Recipients:</div>
                            {isSellerOrAdmin ? (
                                <div className={classes.fieldWrapper}>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '6px 10px', border: '1px solid #c4c4c4', borderRadius: 4, minHeight: 40, alignItems: 'center', backgroundColor: '#fff', cursor: 'text' }}>
                                        {formData.recipientEmailsList.map((email) => (
                                            <span key={email} style={{ display: 'inline-flex', alignItems: 'center', background: '#e0e0e0', borderRadius: 16, padding: '2px 10px', fontSize: 13 }}>
                                                {email}
                                                <button className='sortBtn' onClick={() => removeEmail(email)} style={{padding:'4px', marginLeft: 6, cursor: 'pointer', fontSize: 16, lineHeight: 1, color: '#555' }}>&times;</button>
                                            </span>
                                        ))}
                                        <input
                                            type="text"
                                            value={emailInput}
                                            onChange={(e) => setEmailInput(e.target.value)}
                                            onKeyDown={handleEmailInputKeyDown}
                                            onBlur={() => emailInput.trim() && addEmail(emailInput.trim())}
                                            placeholder={formData.recipientEmailsList.length === 0 ? 'Type email and press Enter or comma' : ''}
                                            style={{ border: 'none', outline: 'none', flex: 1, minWidth: 180, fontSize: 14, background: 'transparent' }}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className={classes.fieldWrapper}>
                                    <FormControl variant="outlined" className={classes.select} size="small">
                                        <ReactSelect
                                            value={formData.recipients}
                                            isMulti
                                            options={recipientList}
                                            onChange={(value) => handleMultipleSelectChange(value, 'recipients')}
                                        />
                                    </FormControl>
                                    {showOtherEmails && (
                                        <OtherRecipientEmailsInput
                                            value={formData.otherEmails}
                                            onChange={(value) => handleChange('otherEmails', value)}
                                            disabled={showLoader}
                                        />
                                    )}
                                </div>
                            )}
                        </div>

                        {!sendOnly && (
                            <div className={classes.formRow}>
                                <div className={classes.label}>Select Template:</div>
                                <div className={classes.fieldWrapper}>
                                    <Select
                                        variant="outlined"
                                        size="small"
                                        className={classes.select}
                                        value={formData.templateId || ''}
                                        onChange={(e) => handleTemplateSelect(e)}
                                        displayEmpty
                                        renderValue={(value) => {
                                            if (!value) return <em style={{ color: '#aaa' }}>Select Template</em>;
                                            const tmpl = templateList.find(t => t.id === value);
                                            return tmpl ? tmpl.name : '';
                                        }}
                                    >
                                        <MenuItem value="">
                                            None
                                        </MenuItem>
                                        {templateList.map((template) => (
                                            <MenuItem key={template.id} value={template.id}>
                                                {template.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </div>
                            </div>
                        )}

                        <div className={classes.formRow}>
                            <div className={classes.label}>From Name:</div>
                            <div className={classes.fieldWrapper}>
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    className={classes.textField}
                                    value={formData.fromName}
                                    onChange={(e) => handleChange('fromName', e.target.value)}
                                    placeholder="e.g., {Name} from {CompanyName}"
                                    inputProps={{ maxLength: SENDER_FIELD_MAX_LENGTH }}
                                    disabled={sendOnly}
                                />
                            </div>
                        </div>

                        <div className={classes.formRow}>
                            <div className={classes.label}>Reply-To Email:</div>
                            <div className={classes.fieldWrapper}>
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    className={classes.textField}
                                    value={formData.replyToEmail}
                                    onChange={(e) => handleChange('replyToEmail', e.target.value)}
                                    placeholder="e.g., name@example.com"
                                    type="email"
                                    inputProps={{ maxLength: SENDER_FIELD_MAX_LENGTH }}
                                    disabled={sendOnly}
                                />
                            </div>
                        </div>

                        <div className={classes.formRow}>
                            <div className={classes.label}>Subject:</div>
                            <div className={classes.fieldWrapper}>
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    className={classes.textField}
                                    value={formData.subject}
                                    onChange={(e) => handleChange('subject', e.target.value)}
                                    disabled={sendOnly}
                                />
                            </div>
                        </div>

                        <div className={classes.formRow}>
                            <div className={classes.label}>Body:</div>
                            <div className={classes.quillWrapper}>
                                {!isSellerOrAdmin && !sendOnly && (
                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8, gap: 8 }}>
                                        <span style={{ fontSize: 13, color: '#555', fontWeight: 500 }}>Insert Variable:</span>
                                        <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
                                            <Select
                                                displayEmpty
                                                value=""
                                                onChange={(e) => {
                                                    insertDynamicValue(e.target.value);
                                                }}
                                                renderValue={() => 'Select a variable'}
                                            >
                                                {emailPlaceholderList.map((v) => (
                                                    <MenuItem key={v.value} value={v.value}>
                                                        {v.label} &nbsp;<span style={{ color: '#888', fontSize: 12 }}>{v.value}</span>
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </div>
                                )}
                                <iframe
                                    ref={iframeRef}
                                    title="Email Body Editor"
                                    style={{
                                        width: '100%',
                                        minHeight: 400,
                                        border: '1px solid #c4c4c4',
                                        borderRadius: 4,
                                        backgroundColor: '#fff',
                                    }}
                                />
                            </div>
                        </div>

                        <div className={classes.formRow}>
                            <div className={classes.label}>Attachments:</div>
                            <div className={classes.fieldWrapper}>
                                <AttachmentsField
                                    attachments={attachments}
                                    onChange={setAttachments}
                                    onUploadingChange={setUploadingAttachments}
                                    disabled={showLoader || loadingAttachments}
                                    templateId={formData.templateId || undefined}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Send / Save & Send Confirmation */}
            <AlertDialogModal
                modalShow={showAlert}
                handleCancelDeleteAlert={() => setShowAlert(false)}
                handleYesDeleteAlert={alertAction === 'saveAndSend' ? handleSaveAndSend : handleSend}
                headingText="Confirm Send"
                messageText="Are you sure you want to send this email?"
            />
            {/* Save Template Modal */}
            <Modal show={saveTemplateModal.show} onHide={closeSaveTemplateModal} centered>
                <Modal.Header closeButton>
                    <span className="modal-m-text">Save Template</span>
                </Modal.Header>
                <Modal.Body>
                    <div className="row modal_input">
                        <div className="col-lg-4 col-sm-12 formLabel">
                            <span className="input-label fnt-wgt-editmgmt">Template Name:</span>
                        </div>
                        <div className="col-lg-8 col-sm-12">
                            <input
                                className="form-control"
                                type="text"
                                autoFocus
                                maxLength={200}
                                value={saveTemplateModal.name}
                                onChange={(e) => setSaveTemplateModal((prev) => ({ ...prev, name: e.target.value }))}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleSaveTemplate(); }}
                                placeholder="Enter template name"
                                aria-label="Template name"
                            />
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn cus-primary-transparent-btn" onClick={closeSaveTemplateModal}>
                        Cancel
                    </button>
                    <button className="btn cus-seconday-bg-btn" onClick={handleSaveTemplate} disabled={!saveTemplateModal.name.trim()}>
                        Save
                    </button>
                </Modal.Footer>
            </Modal>
            {/* Link Editor Modal */}
            <Modal show={linkEditor.show} onHide={() => setLinkEditor({ show: false, href: '', text: '', anchor: null })} centered>
                <Modal.Header closeButton>
                    <span className="modal-m-text">Edit Link</span>
                </Modal.Header>
                <Modal.Body>
                    <div className="row modal_input mb-3">
                        <div className="col-lg-4 col-sm-12 formLabel">
                            <span className="input-label fnt-wgt-editmgmt">Link URL:</span>
                        </div>
                        <div className="col-lg-8 col-sm-12">
                            <input
                                className="form-control"
                                type="text"
                                value={linkEditor.href}
                                onChange={(e) => setLinkEditor((prev) => ({ ...prev, href: e.target.value }))}
                                placeholder="Enter URL"
                            />
                        </div>
                    </div>
                    <div className="row modal_input">
                        <div className="col-lg-4 col-sm-12 formLabel">
                            <span className="input-label fnt-wgt-editmgmt">Link Text:</span>
                        </div>
                        <div className="col-lg-8 col-sm-12">
                            <input
                                className="form-control"
                                type="text"
                                value={linkEditor.text}
                                onChange={(e) => setLinkEditor((prev) => ({ ...prev, text: e.target.value }))}
                                placeholder="Enter display text"
                            />
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button
                        className="btn cus-primary-transparent-btn"
                        onClick={() => setLinkEditor({ show: false, href: '', text: '', anchor: null })}
                    >
                        Cancel
                    </button>
                    <button
                        className="btn cus-seconday-bg-btn"
                        onClick={() => {
                            const { anchor, href, text } = linkEditor;
                            if (anchor) {
                                anchor.setAttribute('href', href);
                                anchor.textContent = text;
                                handleChange('body', getIframeBody());
                            }
                            setLinkEditor({ show: false, href: '', text: '', anchor: null });
                        }}
                    >
                        Save
                    </button>
                </Modal.Footer>
            </Modal>
        </main>
    );
};

export default ComposeEmail;